import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BUILDER_PROJECTS } from "@/data/builderProjects";

const AUTO_PLAY_INTERVAL = 4200;
const ITEM_HEIGHT = 62;

const wrap = (min, max, v) => {
  const range = max - min;
  return ((((v - min) % range) + range) % range) + min;
};

export default function BuilderProjectsCarousel() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { categoryIndex, slide }

  const len = BUILDER_PROJECTS.length;
  const currentIndex = ((step % len) + len) % len;
  const active = BUILDER_PROJECTS[currentIndex];

  const next = useCallback(() => setStep((s) => s + 1), []);

  useEffect(() => {
    if (paused || lightbox) return;
    const id = setInterval(next, AUTO_PLAY_INTERVAL);
    return () => clearInterval(id);
  }, [next, paused, lightbox]);

  const handleChipClick = (i) => {
    const diff = (i - currentIndex + len) % len;
    if (diff !== 0) setStep((s) => s + diff);
    else setLightbox({ categoryIndex: i, slide: 0 });
  };

  const openLightbox = () =>
    setLightbox({ categoryIndex: currentIndex, slide: 0 });

  const closeLightbox = () => setLightbox(null);

  const lightboxCategory =
    lightbox != null ? BUILDER_PROJECTS[lightbox.categoryIndex] : null;
  const lightboxImages = lightboxCategory?.images || [];

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight")
        setLightbox((l) =>
          l ? { ...l, slide: (l.slide + 1) % lightboxImages.length } : l
        );
      if (e.key === "ArrowLeft")
        setLightbox((l) =>
          l
            ? {
                ...l,
                slide:
                  (l.slide - 1 + lightboxImages.length) % lightboxImages.length,
              }
            : l
        );
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, lightboxImages.length]);

  const getCardStatus = (i) => {
    let d = i - currentIndex;
    if (d > len / 2) d -= len;
    if (d < -len / 2) d += len;
    if (d === 0) return "active";
    if (d === -1) return "prev";
    if (d === 1) return "next";
    return "hidden";
  };

  return (
    <div
      data-testid="builder-projects-carousel"
      className="w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative overflow-hidden rounded-sm flex flex-col lg:flex-row min-h-[620px] lg:min-h-[560px] border border-cream/15 bg-charcoal">
        {/* LEFT — chip list */}
        <div className="relative w-full lg:w-[44%] min-h-[340px] lg:min-h-full flex flex-col items-start justify-center overflow-hidden px-8 md:px-14 py-12 lg:py-0 bg-[#1f1d1a]">
          <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#1f1d1a] to-transparent z-20 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#1f1d1a] to-transparent z-20 pointer-events-none" />

          <p className="text-[11px] tracking-[0.32em] uppercase text-brass mb-10">
            Project types
          </p>

          <div className="relative w-full h-[260px] lg:h-[420px] flex items-center justify-start z-10">
            {BUILDER_PROJECTS.map((p, i) => {
              const distance = i - currentIndex;
              const wrappedDistance = wrap(-(len / 2), len / 2, distance);
              const isActive = i === currentIndex;
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.id}
                  style={{ height: ITEM_HEIGHT, width: "fit-content" }}
                  animate={{
                    y: wrappedDistance * ITEM_HEIGHT,
                    opacity: 1 - Math.abs(wrappedDistance) * 0.28,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 90,
                    damping: 22,
                    mass: 1,
                  }}
                  className="absolute flex items-center justify-start"
                >
                  <button
                    data-testid={`builder-carousel-chip-${p.id}`}
                    onClick={() => handleChipClick(i)}
                    className={`relative flex items-center gap-4 px-6 md:px-8 py-3.5 md:py-4 rounded-full border transition-all duration-500 text-left ${
                      isActive
                        ? "bg-brass text-charcoal border-brass shadow-[0_8px_30px_rgba(198,168,124,0.25)]"
                        : "bg-transparent text-cream/55 border-cream/15 hover:text-cream hover:border-cream/40"
                    }`}
                  >
                    <Icon
                      size={18}
                      strokeWidth={1.5}
                      className={isActive ? "text-charcoal" : "text-cream/50"}
                    />
                    <span className="text-[12px] md:text-[13px] tracking-[0.18em] uppercase font-medium whitespace-nowrap">
                      {p.label}
                    </span>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — image card */}
        <div className="flex-1 min-h-[420px] lg:h-auto relative flex items-center justify-center py-12 md:py-16 lg:py-12 px-6 md:px-12 overflow-hidden border-t lg:border-t-0 lg:border-l border-cream/10 bg-[#161514]">
          <div className="relative w-full max-w-[460px] aspect-[4/5] flex items-center justify-center">
            {BUILDER_PROJECTS.map((p, i) => {
              const status = getCardStatus(i);
              const isActive = status === "active";
              const isPrev = status === "prev";
              const isNext = status === "next";
              return (
                <motion.div
                  key={p.id}
                  initial={false}
                  animate={{
                    x: isActive ? 0 : isPrev ? -100 : isNext ? 100 : 0,
                    scale: isActive ? 1 : isPrev || isNext ? 0.84 : 0.7,
                    opacity: isActive ? 1 : isPrev || isNext ? 0.35 : 0,
                    rotate: isPrev ? -3 : isNext ? 3 : 0,
                    zIndex: isActive ? 20 : isPrev || isNext ? 10 : 0,
                    pointerEvents: isActive ? "auto" : "none",
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 25,
                    mass: 0.8,
                  }}
                  className="absolute inset-0 rounded-sm overflow-hidden border-[6px] border-charcoal bg-charcoal origin-center cursor-pointer group"
                  onClick={isActive ? openLightbox : undefined}
                  role={isActive ? "button" : undefined}
                  tabIndex={isActive ? 0 : -1}
                  onKeyDown={(e) =>
                    isActive && e.key === "Enter" ? openLightbox() : null
                  }
                  data-testid={isActive ? "builder-carousel-active-card" : undefined}
                >
                  <img
                    src={p.images[0]}
                    alt={p.label}
                    className={`w-full h-full object-cover transition-all duration-700 ${
                      isActive
                        ? "grayscale-0 blur-0 group-hover:scale-[1.04]"
                        : "grayscale blur-[2px] brightness-75"
                    }`}
                  />
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.5 }}
                        className="absolute inset-x-0 bottom-0 p-8 md:p-10 pt-28 bg-gradient-to-t from-charcoal via-charcoal/55 to-transparent flex flex-col gap-3"
                      >
                        <div className="bg-cream text-charcoal px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.22em] w-fit">
                          {String(i + 1).padStart(2, "0")} · {p.label}
                        </div>
                        <p className="text-cream font-display text-xl md:text-2xl leading-snug max-w-sm">
                          {p.description}
                        </p>
                        <p className="mt-2 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-brass">
                          Click photo to view more →
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {isActive && (
                    <div className="absolute top-6 left-6 flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-brass shadow-[0_0_10px_#c6a87c]" />
                      <span className="text-cream/85 text-[10px] uppercase tracking-[0.3em]">
                        {p.images.length} photos
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightboxCategory && (
          <motion.div
            data-testid="builder-carousel-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-charcoal/95 backdrop-blur-sm flex flex-col"
            onClick={closeLightbox}
          >
            <div
              className="flex items-center justify-between px-6 md:px-10 py-5 md:py-6 text-cream"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-brass">
                  Project type
                </p>
                <h3 className="mt-2 font-display text-2xl md:text-4xl">
                  {lightboxCategory.label}
                </h3>
              </div>
              <button
                data-testid="builder-carousel-lightbox-close"
                onClick={closeLightbox}
                aria-label="Close"
                className="text-[11px] tracking-[0.28em] uppercase border border-cream/40 hover:border-brass hover:text-brass px-4 py-2 transition-colors"
              >
                Close ✕
              </button>
            </div>

            <div
              className="relative flex-1 flex items-center justify-center px-4 md:px-12 pb-4"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                aria-label="Previous image"
                data-testid="builder-carousel-lightbox-prev"
                onClick={() =>
                  setLightbox((l) =>
                    l
                      ? {
                          ...l,
                          slide:
                            (l.slide - 1 + lightboxImages.length) %
                            lightboxImages.length,
                        }
                      : l
                  )
                }
                className="absolute left-3 md:left-8 z-10 h-12 w-12 border border-cream/30 text-cream hover:border-brass hover:text-brass flex items-center justify-center"
              >
                ←
              </button>
              <div className="relative w-full h-full max-w-6xl max-h-[72vh] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={lightbox.slide}
                    src={lightboxImages[lightbox.slide]}
                    alt={`${lightboxCategory.label} — ${lightbox.slide + 1}`}
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45 }}
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                </AnimatePresence>
              </div>
              <button
                aria-label="Next image"
                data-testid="builder-carousel-lightbox-next"
                onClick={() =>
                  setLightbox((l) =>
                    l
                      ? {
                          ...l,
                          slide: (l.slide + 1) % lightboxImages.length,
                        }
                      : l
                  )
                }
                className="absolute right-3 md:right-8 z-10 h-12 w-12 border border-cream/30 text-cream hover:border-brass hover:text-brass flex items-center justify-center"
              >
                →
              </button>
            </div>

            <div
              className="flex items-center justify-between px-6 md:px-10 py-5 text-cream/70"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-[11px] tracking-[0.28em] uppercase">
                {String(lightbox.slide + 1).padStart(2, "0")} /{" "}
                {String(lightboxImages.length).padStart(2, "0")}
              </span>
              <div className="flex gap-2">
                {lightboxImages.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Image ${i + 1}`}
                    onClick={() => setLightbox((l) => (l ? { ...l, slide: i } : l))}
                    className={`h-1.5 transition-all ${
                      i === lightbox.slide ? "w-10 bg-brass" : "w-5 bg-cream/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
